import { Check, Copy } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Checkbox } from "@tokenterminal/core/ui/input/checkbox/Checkbox"
import {
  ToggleButton,
  ToggleButtonGroup,
} from "@tokenterminal/core/ui/input/toggle/ToggleButtonGroup"

import { Divider } from "@tokenterminal/core/ui/layout/divider/Divider"
import toast from "modules/account/views/Team/toast"
import { type SetStateAction, type Dispatch, useState } from "react"
import BackgroundColorSelector from "./sidebar/BackgroundColorSelector"
import { CustomImageUpload } from "./sidebar/CustomImageUpload"
import { ProjectLogoDropdown } from "./sidebar/ProjectLogoDropdown"
import { type ChartImageState } from "./types"

export const ChartConfigSidebar = ({
  state,
  setState,
  sourcesAndDefinitionsText,
  footnoteText,
}: {
  state: ChartImageState
  setState: Dispatch<SetStateAction<ChartImageState>>
  sourcesAndDefinitionsText: string
  footnoteText?: string
}) => {
  const [copied, setCopied] = useState(false)

  return (
    <div className="flex flex-col gap-5 w-[300px] p-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 justify-between">
          <label className="tt-field-label text-fg-secondary">
            Header image
          </label>
        </div>
        <ToggleButtonGroup
          selectedKey={state.image?.type}
          size="small"
          onSelectionChange={(key) => {
            setState({
              ...state,
              image: {
                ...state.image,
                type: key as "project" | "custom",
              },
            })
          }}
          className="w-full"
        >
          <ToggleButton className="w-full" id="none">
            None
          </ToggleButton>
          <ToggleButton className="w-full" id="project">
            Project logo
          </ToggleButton>
          <ToggleButton className="w-full" id="custom">
            Custom
          </ToggleButton>
        </ToggleButtonGroup>
        {state.image?.type === "project" && (
          <ProjectLogoDropdown state={state} setState={setState} />
        )}
        {state.image?.type === "custom" && (
          <div className="mt-2">
            <CustomImageUpload
              // @ts-ignore
              file={state.image.customUrl}
              setFile={(file) => {
                setState({
                  ...state,
                  image: {
                    ...state.image,
                    type: "custom",
                    customUrl: file ?? undefined,
                  },
                })
              }}
            />
          </div>
        )}
      </div>
      <Divider />
      <Checkbox
        id="show-background"
        isSelected={state.showBackground}
        onChange={(checked) =>
          setState((prev) => {
            let nextSourcesDisplay = prev.sourcesAndFootnoteDisplay
            if (!checked && prev.sourcesAndFootnoteDisplay === "out-of-card") {
              nextSourcesDisplay = "inside-card"
            }

            return {
              ...prev,
              showBackground: !!checked,
              sourcesAndFootnoteDisplay: nextSourcesDisplay,
            }
          })
        }
      >
        Show background
      </Checkbox>
      {state.showBackground && (
        <BackgroundColorSelector state={state} setState={setState} />
      )}
      <Divider />
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 justify-between">
          <label className="tt-field-label text-fg-secondary">
            Sources & footnotes
          </label>
          <div>
            {!state.sourcesAndFootnoteDisplay && (
              <Button
                variant="ghost"
                size="xsmall"
                className="text-fg-secondary"
                onPress={() => {
                  setCopied(true)
                  toast({
                    body: "Sources copied to clipboard",
                    icon: Check,
                  })
                  navigator.clipboard.writeText(sourcesAndDefinitionsText)
                  setTimeout(() => {
                    setCopied(false)
                  }, 1000)
                }}
              >
                Copy text
                <Icon as={copied ? Check : Copy} />
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <ToggleButtonGroup
            selectedKey={state.sourcesAndFootnoteDisplay ?? "none"}
            size="small"
            onSelectionChange={(key) => {
              if (key === "none") {
                setState({
                  ...state,
                  sourcesAndFootnoteDisplay: null,
                })
              } else {
                setState({
                  ...state,
                  sourcesAndFootnoteDisplay: key as
                    | "inside-card"
                    | "out-of-card",
                })
              }
            }}
            className="w-full"
          >
            <ToggleButton className="w-full" id="none">
              Hidden
            </ToggleButton>
            <ToggleButton className="w-full" id="inside-card">
              Inside
            </ToggleButton>
            <ToggleButton className="w-full" id="out-of-card">
              Outside
            </ToggleButton>
          </ToggleButtonGroup>
          {state.sourcesAndFootnoteDisplay !== null && (
            <div className="flex flex-col gap-3">
              <Checkbox
                id="show-sources"
                className="ml-2"
                isSelected={state.showSources}
                onChange={(checked) => {
                  setState({ ...state, showSources: checked })
                }}
              >
                Show sources
              </Checkbox>
              <Checkbox
                id="show-footnote"
                className="ml-2"
                isSelected={!footnoteText ? false : state.showFootnote}
                onChange={(checked) => {
                  setState({ ...state, showFootnote: checked })
                }}
                isDisabled={!footnoteText}
              >
                Show footnote
              </Checkbox>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
